import {
  cloneElement,
  Component,
  CSSProperties,
  forwardRef,
  isValidElement,
  PropsWithChildren,
  ReactElement,
  ReactNode,
  Ref,
  useCallback,
  useMemo,
} from 'react';
import RcTree, {
  BasicDataNode,
  TreeNode as RcTreeNode,
  TreeProps as RcTreeProps,
} from 'rc-tree';
import classNames from 'classnames';
import { DataNode, Key } from 'rc-tree/lib/interface';
import {
  AiFillCaretDown,
  AiOutlineFile,
  AiOutlineHolder,
  AiOutlineLoading,
  AiOutlineMinusSquare,
  AiOutlinePlusSquare,
} from 'react-icons/ai/index.js';
import 'rc-tree/assets/index.css';
import { Icon } from '@chakra-ui/react';

export interface TreeNodeAttribute {
  eventKey: string;
  prefixCls: string;
  className: string;
  expanded: boolean;
  selected: boolean;
  checked: boolean;
  halfChecked: boolean;
  children: ReactNode;
  title: ReactNode;
  pos: string;
  dragOver: boolean;
  dragOverGapTop: boolean;
  dragOverGapBottom: boolean;
  isLeaf: boolean;
  selectable: boolean;
  disabled: boolean;
  disableCheckbox: boolean;
}

export interface CustomTreeNodeProps {
  className?: string;
  checkable?: boolean;
  disabled?: boolean;
  disableCheckbox?: boolean;
  title?: string | ReactNode;
  key?: Key;
  eventKey?: string;
  isLeaf?: boolean;
  checked?: boolean;
  expanded?: boolean;
  loading?: boolean;
  selected?: boolean;
  selectable?: boolean;
  icon?: ((treeNode: TreeNodeAttribute) => ReactNode) | ReactNode;
  children?: ReactNode;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [customProp: string]: any;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type TreeNode = Component<CustomTreeNodeProps, any>;

export type TreeDraggableFn = (node: TreeNode) => boolean;
export interface DraggableConfig {
  icon?: ReactNode | false;
  nodeDraggable?: TreeDraggableFn;
}
export type DropIndicatorRenderArgs = {
  dropPosition: -1 | 0 | 1;
  dropLevelOffset: number;
  indent: number;
  direction: RcTreeProps['direction'];
};

export interface TreeProps<T extends BasicDataNode = DataNode>
  extends Omit<
    RcTreeProps<T>,
    'prefixCls' | 'showLine' | 'dropIndicatorRender' | 'draggable'
  > {
  prefixCls?: string;
  showLine?: boolean | { showLeafIcon: boolean };
  className?: string;
  multiple?: boolean;
  autoExpandParent?: boolean;
  checkStrictly?: boolean;
  checkable?: boolean;
  disabled?: boolean;
  defaultExpandAll?: boolean;
  defaultExpandParent?: boolean;
  defaultExpandedKeys?: Key[];
  expandedKeys?: Key[];
  checkedKeys?: Key[] | { checked: Key[]; halfChecked: Key[] };
  defaultCheckedKeys?: Key[];
  selectedKeys?: Key[];
  defaultSelectedKeys?: Key[];
  selectable?: boolean;
  filterApTreeNode?: (node: TreeNode) => boolean;
  loadedKeys?: Key[];
  draggable?: TreeDraggableFn | boolean | DraggableConfig;
  style?: CSSProperties;
  showIcon?: boolean;
  icon?: ((nodeProps: TreeNodeAttribute) => ReactNode) | ReactNode;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  switcherIcon?: ReactElement<any>;
  children?: ReactNode;
  blockNode?: boolean;
  direction?: RcTreeProps['direction'];
  dropIndicatorRender?: (args: DropIndicatorRenderArgs) => ReactNode;
}

type CompoundedComponent = (<T extends BasicDataNode | DataNode = DataNode>(
  props: PropsWithChildren<TreeProps<T>> & { ref?: Ref<RcTree> },
) => ReactElement) & {
  TreeNode: typeof RcTreeNode;
};

const offset = 4;

function dropIndicatorRenderUtil(props: DropIndicatorRenderArgs) {
  const { dropPosition, dropLevelOffset, indent, direction = 'ltr' } = props;
  const startPosition = direction === 'ltr' ? 'left' : 'right';
  const endPosition = direction === 'ltr' ? 'right' : 'left';
  const style: CSSProperties = {
    [startPosition]: -dropLevelOffset * indent + offset,
    [endPosition]: 0,
  };
  switch (dropPosition) {
    case -1:
      style.top = -3;
      break;
    case 1:
      style.bottom = -3;
      break;
    default:
      // dropPosition === 0
      style.bottom = -3;
      style[startPosition] = indent + offset;
      break;
  }
  return <div style={style} className={`rc-tree-drop-indicator`} />;
}

export const renderSwitcherIcon = (
  switcherIcon: ReactNode,
  showLine: boolean | { showLeafIcon: boolean } | undefined,
  { isLeaf, expanded, loading }: CustomTreeNodeProps,
): ReactNode => {
  if (loading) {
    return (
      <Icon as={AiOutlineLoading} className={`rc-tree-switcher-loading-icon`} />
    );
  }
  let showLeafIcon;
  if (showLine && typeof showLine === 'object') {
    showLeafIcon = showLine.showLeafIcon;
  }
  if (isLeaf) {
    if (showLine) {
      if (typeof showLine === 'object' && !showLeafIcon) {
        return <span className={`rc-tree-switcher-leaf-line`} />;
      }
      return (
        <Icon as={AiOutlineFile} className={`rc-tree-switcher-line-icon`} />
      );
    }
    return null;
  }
  const switcherCls = `rc-tree-switcher-icon`;
  if (isValidElement(switcherIcon)) {
    return cloneElement(switcherIcon, {
      className: classNames(switcherIcon.props.className || '', switcherCls),
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } as any);
  }

  if (switcherIcon) {
    return switcherIcon;
  }

  if (showLine) {
    return expanded ? (
      <Icon
        as={AiOutlineMinusSquare}
        className={`rc-tree-switcher-line-icon`}
      />
    ) : (
      <Icon as={AiOutlinePlusSquare} className={`rc-tree-switcher-line-icon`} />
    );
  }
  return <Icon as={AiFillCaretDown} className={switcherCls} />;
};

export const Tree = forwardRef<RcTree, TreeProps>((props, ref) => {
  const {
    direction,
    virtual,
    className,
    showIcon,
    showLine,
    switcherIcon,
    blockNode,
    children,
    checkable,
    selectable,
    draggable,
    dropIndicatorRender = dropIndicatorRenderUtil,
    prefixCls = 'rc-tree',
    ...rest
  } = props;

  const newProps = {
    ...props,
    showLine: Boolean(showLine),
  };

  const modifiedDropIndicatorRender = useCallback(
    (
      args: DropIndicatorRenderArgs & {
        prefixCls: string;
      },
    ) => {
      const { prefixCls: _prefixCls, ...rest } = args;
      return dropIndicatorRender(rest);
    },
    [dropIndicatorRender],
  );

  const draggableConfig = useMemo(() => {
    if (!draggable) {
      return false;
    }

    let mergedDraggable: DraggableConfig = {};
    switch (typeof draggable) {
      case 'function':
        mergedDraggable.nodeDraggable = draggable;
        break;

      case 'object':
        mergedDraggable = { ...draggable };
        break;

      default:
      // Do nothing
    }

    if (mergedDraggable.icon !== false) {
      mergedDraggable.icon = mergedDraggable.icon || (
        <Icon as={AiOutlineHolder} />
      );
    }

    return mergedDraggable;
  }, [draggable]);

  return (
    <RcTree
      itemHeight={20}
      ref={ref}
      virtual={virtual}
      {...newProps}
      prefixCls={prefixCls}
      className={classNames(
        'chakra-tree',
        {
          [`chakra-tree-icon-hide`]: !showIcon,
          [`chakra-tree-block-node`]: blockNode,
          [`chakra-tree-unselectable`]: !selectable,
          [`chakra-tree-rtl`]: direction === 'rtl',
        },
        className,
      )}
      direction={direction}
      checkable={
        checkable ? (
          <span className={`chakra-tree-checkbox-inner`} />
        ) : (
          checkable
        )
      }
      selectable={selectable}
      switcherIcon={(nodeProps: CustomTreeNodeProps) =>
        renderSwitcherIcon(switcherIcon, showLine, nodeProps)
      }
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      draggable={draggableConfig as any}
      dropIndicatorRender={modifiedDropIndicatorRender}
      {...rest}
    >
      {children}
    </RcTree>
  );
}) as unknown as CompoundedComponent;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
(Tree as any).defaultProps = {
  checkable: false,
  selectable: false,
  showIcon: false,
  blockNode: false,
};
Tree.TreeNode = RcTreeNode;

export type { BasicDataNode, TreeNodeProps } from 'rc-tree/lib';
